// ============================================================================
// Fee Category Service
// Connects to hcp-admin-backend APIs for Fee Categories
// Uses fetchWithAuth for automatic token refresh on 401
// ============================================================================

import { fetchWithAuth } from './auth';

// Base URL for API - should be configured via environment variable
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8004/api';

// ============================================================================
// Types
// ============================================================================

export interface FeeChargeCategory {
  id: number;
  category_name: string;
  category_type: 'site_level' | 'store_level' | 'both' | 'special';
  display_order: number;
  is_taxable_default: boolean;
  is_refundable_default: boolean;
  active: boolean;
  description?: string;
  created_at?: string;
  updated_at?: string;
}

export interface StoreFeeCategoryConfig {
  id: number;
  store_id: number;
  fee_category_id: number;
  is_taxable?: boolean | null;
  is_refundable?: boolean | null;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface StoreTaxAppliedFee {
  id: number;
  store_id: number;
  store_tax_id: number;
  fee_category_id: number;
  active: boolean;
  created_at?: string;
  updated_at?: string;
  // Joined data
  fee_category?: FeeChargeCategory;
}

export interface StoreTaxAppliedFeeCreate {
  store_id: number;
  store_tax_id: number;
  fee_category_id: number;
  active?: boolean;
}

/**
 * Combined category with effective values (store overrides applied)
 */
export interface EffectiveFeeCategory {
  id: number;
  category_name: string;
  category_type: 'site_level' | 'store_level' | 'both' | 'special';
  display_order: number;
  description?: string;
  is_taxable: boolean;
  is_refundable: boolean;
  active: boolean;
  has_store_override: boolean;
}

// ============================================================================
// Display Helpers
// ============================================================================

const CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  'base_cost': 'Base Cost',
  'occupancy_fee': 'Occupancy Fee',
  'pet_fee': 'Pet Fee',
  'vehicle_fee': 'Vehicle Fee',
  'service_fee': 'Service Fee',
  'booking_fee': 'Booking Fee',
  'misc_fee': 'Miscellaneous Fee',
  'misc_fee_taxable': 'Miscellaneous Fee (Taxable)',
  'site_imposed_fee': 'Site-Specific Fee',
  'site_imposed_fee_taxable': 'Site-Specific Fee (Taxable)',
  'modification_fee': 'Modification Fee',
  'cancellation_fee': 'Cancellation Fee',
  'tax': 'Tax',
};

/**
 * Format category name for display
 */
export function formatCategoryName(categoryName: string): string {
  if (CATEGORY_DISPLAY_NAMES[categoryName]) {
    return CATEGORY_DISPLAY_NAMES[categoryName];
  }
  // Convert snake_case to Title Case
  return categoryName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// ============================================================================
// Fee Charge Categories API
// ============================================================================

/**
 * Get all fee charge categories
 */
export async function getFeeChargeCategories(activeOnly: boolean = true): Promise<FeeChargeCategory[]> {
  const categories: FeeChargeCategory[] = await fetchWithAuth(`${API_BASE_URL}/fee-charge-categories/`);
  if (activeOnly) {
    return categories.filter(c => c.active);
  }
  return categories;
}

/**
 * Get a single fee charge category by ID
 */
export async function getFeeChargeCategoryById(categoryId: number): Promise<FeeChargeCategory> {
  return fetchWithAuth(`${API_BASE_URL}/fee-charge-categories/${categoryId}`);
}

// ============================================================================
// Store Fee Category Config API
// ============================================================================

/**
 * Get store-specific fee category configs
 */
export async function getStoreFeeCategoryConfigs(storeId: number): Promise<StoreFeeCategoryConfig[]> {
  return fetchWithAuth(`${API_BASE_URL}/store-fee-category-config/?store_id=${storeId}`);
}

// ============================================================================
// Combined/Effective Categories
// ============================================================================

/**
 * Get fee categories with store-specific overrides applied
 * This merges fee_charge_categories with store_fee_category_config
 */
export async function getEffectiveFeeCategories(
  storeId: number,
  options: {
    categoryType?: 'site_level' | 'store_level' | 'both' | 'special';
    activeOnly?: boolean;
    taxableOnly?: boolean;
  } = {}
): Promise<EffectiveFeeCategory[]> {
  const { categoryType, activeOnly = true, taxableOnly = false } = options;

  // Fetch both tables in parallel
  const [categories, storeConfigs] = await Promise.all([
    getFeeChargeCategories(false), // Get all, we'll filter later
    getStoreFeeCategoryConfigs(storeId),
  ]);

  // Create a map of store configs by fee_category_id
  const configMap = new Map<number, StoreFeeCategoryConfig>();
  storeConfigs.forEach(config => {
    configMap.set(config.fee_category_id, config);
  });

  // Merge categories with store overrides
  let effectiveCategories: EffectiveFeeCategory[] = categories.map(category => {
    const storeConfig = configMap.get(category.id);
    const hasOverride = !!storeConfig;

    return {
      id: category.id,
      category_name: category.category_name,
      category_type: category.category_type,
      display_order: category.display_order,
      description: category.description,
      // Use store override if available, otherwise use default
      is_taxable: storeConfig?.is_taxable ?? category.is_taxable_default,
      is_refundable: storeConfig?.is_refundable ?? category.is_refundable_default,
      active: storeConfig ? storeConfig.active : category.active,
      has_store_override: hasOverride,
    };
  });

  // Apply filters
  if (activeOnly) {
    effectiveCategories = effectiveCategories.filter(c => c.active);
  }

  if (categoryType) {
    effectiveCategories = effectiveCategories.filter(
      c => c.category_type === categoryType || c.category_type === 'both'
    );
  }

  if (taxableOnly) {
    effectiveCategories = effectiveCategories.filter(c => c.is_taxable);
  }

  // Sort by display_order
  effectiveCategories.sort((a, b) => a.display_order - b.display_order);

  return effectiveCategories;
}

/**
 * Get store-level fee categories (for FeesCard)
 */
export async function getStoreLevelFeeCategories(storeId: number): Promise<EffectiveFeeCategory[]> {
  return getEffectiveFeeCategories(storeId, { categoryType: 'store_level' });
}

/**
 * Get taxable fee categories (for TaxCard - selecting which fees a tax applies to)
 */
export async function getTaxableFeeCategories(storeId: number): Promise<EffectiveFeeCategory[]> {
  return getEffectiveFeeCategories(storeId, { taxableOnly: true });
}

// ============================================================================
// Store Tax Applied Fees API
// ============================================================================

/**
 * Get tax-to-category mappings for a store
 */
export async function getStoreTaxAppliedFees(
  storeId: number,
  storeTaxId?: number
): Promise<StoreTaxAppliedFee[]> {
  let url = `${API_BASE_URL}/store-tax-applied-fees/?store_id=${storeId}`;
  if (storeTaxId) {
    url += `&store_tax_id=${storeTaxId}`;
  }
  return fetchWithAuth(url);
}

/**
 * Create a tax-to-category mapping
 */
export async function createStoreTaxAppliedFee(
  data: StoreTaxAppliedFeeCreate
): Promise<StoreTaxAppliedFee> {
  return fetchWithAuth(`${API_BASE_URL}/store-tax-applied-fees/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

/**
 * Delete a tax-to-category mapping
 */
export async function deleteStoreTaxAppliedFee(id: number): Promise<void> {
  await fetchWithAuth(`${API_BASE_URL}/store-tax-applied-fees/${id}`, {
    method: 'DELETE',
  });
}

/**
 * Update tax-to-category mappings for a tax
 * Compares current mappings with desired mappings and syncs them
 */
export async function syncTaxAppliedFeeCategories(
  storeId: number,
  storeTaxId: number,
  selectedCategoryIds: number[]
): Promise<void> {
  // Get current mappings for this tax
  const currentMappings = await getStoreTaxAppliedFees(storeId, storeTaxId);
  const currentCategoryIds = currentMappings.map(m => m.fee_category_id);

  // Determine what to add and remove
  const toAdd = selectedCategoryIds.filter(id => !currentCategoryIds.includes(id));
  const toRemove = currentMappings.filter(m => !selectedCategoryIds.includes(m.fee_category_id));

  // Execute additions and removals in parallel
  const addPromises = toAdd.map(categoryId =>
    createStoreTaxAppliedFee({
      store_id: storeId,
      store_tax_id: storeTaxId,
      fee_category_id: categoryId,
      active: true,
    })
  );

  const removePromises = toRemove.map(mapping =>
    deleteStoreTaxAppliedFee(mapping.id)
  );

  await Promise.all([...addPromises, ...removePromises]);
}

// ============================================================================
// Overage Fee Type to Category Mapping
// ============================================================================

const OVERAGE_FEE_TYPE_TO_CATEGORY: Record<string, string> = {
  'adult': 'occupancy_fee',
  'child': 'occupancy_fee',
  'pet': 'pet_fee',
  'vehicle': 'vehicle_fee',
};

/**
 * Get the fee category ID for an overage fee type
 */
export async function getCategoryIdForOverageFeeType(
  feeType: string,
  categories: EffectiveFeeCategory[]
): Promise<number | undefined> {
  const categoryName = OVERAGE_FEE_TYPE_TO_CATEGORY[feeType];
  if (!categoryName) return undefined;

  const category = categories.find(c => c.category_name === categoryName);
  return category?.id;
}

// ============================================================================
// Export service object for convenience
// ============================================================================

export const feeCategoryService = {
  // Categories
  getFeeChargeCategories,
  getFeeChargeCategoryById,
  getStoreFeeCategoryConfigs,
  getEffectiveFeeCategories,
  getStoreLevelFeeCategories,
  getTaxableFeeCategories,
  // Tax applied fees
  getStoreTaxAppliedFees,
  createStoreTaxAppliedFee,
  deleteStoreTaxAppliedFee,
  syncTaxAppliedFeeCategories,
  // Helpers
  formatCategoryName,
  getCategoryIdForOverageFeeType,
};
